import classNames from 'class-names';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';

import { Icon } from '../../../Atoms/Icon';
import Input from '../../../lib/Form/Input';
import useSmallScreen from '../../../lib/hooks/useSmallScreen';

import ActivateSimModal from './ActivateSimModal';
import simIcons, {
  slotStateError,
  slotStateErrorMsg,
  slotStateNotSelected,
  slotStateSelected,
  slotStateUnknownMessage,
  validSlotKeys,
  validSlotStates,
} from './cell-settings-shared';
import EditIcon from './EditIcon';
import { simSchema } from './sim-validate';
import SimInstructionsModal from './SimInstructionsModal';

const SimCards = ({
  readOnly = false,
  slotStatus,
  onSlotUpdate,
  onSlotSelectAndReboot,
  onSlotSelectAndPowerOff,
}) => {
  const isSmallScreen = useSmallScreen();

  const [isEditing, setIsEditing] = useState(false);
  const [editingSimSlot, setEditingSimSlot] = useState(null);
  const [activateSimModal, setActivateSimModal] = useState(false);
  const [simInstructionsModal, setSimInstructionsModal] = useState(false);
  const [simSlots, setSimSlots] = useState([]);

  if (simSlots !== slotStatus) {
    setSimSlots(slotStatus);
  }

  const [activeSimSlot, setActiveSimSlot] = useState(null);
  const [selectedSimSlot, setSelectedSimSlot] = useState(null);

  if (simSlots.length > 0 && !activeSimSlot) {
    const newActiveSimSlot = simSlots.find((slot) => slot.selected)?.slotName;
    setActiveSimSlot(newActiveSimSlot);
    setSelectedSimSlot(newActiveSimSlot);
  }

  const editSimSlot = (slotName) => {
    if (isEditing) {
      if (editingSimSlot === slotName) {
        setIsEditing(false);
        setEditingSimSlot(null);
      } else {
        setEditingSimSlot(slotName);
      }
    } else {
      setIsEditing(true);
      setEditingSimSlot(slotName);
    }
  };

  const openActivateSimModal = (selectedSlotName) => {
    setSelectedSimSlot(selectedSlotName);
    if (selectedSlotName !== activeSimSlot) {
      setActivateSimModal(true);
    }
  };

  const onHideActivateSimModal = (showModal, resetSlot = true) => {
    if (resetSlot) {
      setSelectedSimSlot(activeSimSlot);
    }
    setActivateSimModal(showModal);
  };

  const openSimInstructionsModal = () => {
    setSimInstructionsModal(true);
  };

  const updateSimInfo = (slotIndex, newName, newAPN) => {
    if (isEditing) {
      simSlots[slotIndex].slotConfig.cardName = newName;
      simSlots[slotIndex].slotConfig.apn = newAPN;
      onSlotUpdate(simSlots[slotIndex].slotConfig.slotKey, newName, newAPN);
    }
    editSimSlot(null);
  };

  const getSlotInfo = (slotName) => {
    const slotConfig = simSlots.find(
      (slot) =>
        slot?.slotName === slotName && slot?.slotConfig?.cardName !== '',
    )?.slotConfig;
    return {
      cardName: slotConfig?.cardName,
      slotName,
      slotKey: slotConfig?.slotKey,
      isFixed: slotConfig?.isFixed || false,
    };
  };

  const displayInput = (type, slotName, field, selected) => {
    if (isEditing && editingSimSlot === slotName) {
      return (
        <Input
          name={type.toLowerCase()}
          className="justify-content-start"
          groupClassName="padded-control my-2"
        />
      );
    }
    if (field) {
      return field;
    }
    if (selected) {
      return '-';
    }
    return <em>{type} Required for Activation</em>;
  };

  const populateSelectBox = () => {
    return (
      <Form className="flex-grow-1">
        <select
          className="form-control custom-select"
          value={selectedSimSlot}
          onChange={(event) => {
            openActivateSimModal(event.target.value);
          }}
        >
          {simSlots.map((sim) => (
            <option
              key={sim.slotName}
              value={sim.slotName}
              disabled={!(sim.slotConfig.cardName && sim.slotConfig.apn)}
            >
              {sim.slotConfig.cardName && sim.slotConfig.apn
                ? sim.slotConfig.cardName
                : 'Name/APN Needed for Activation'}
            </option>
          ))}
        </select>
      </Form>
    );
  };

  const populateTableRow = (
    rowName,
    value,
    selected,
    slotName,
    canEdit = false,
    isFirstRow = false,
  ) => {
    const rowValue = canEdit
      ? displayInput(rowName, slotName, value, selected)
      : value || '-';

    return (
      <tr>
        <th
          className="px-2 py-0 bg-light border text-uppercase font-weight-bold text-gray small"
          style={{ width: '20%', minWidth: '83px' }}
        >
          {rowName}
        </th>
        <td
          className={classNames('px-2 py-0 border', {
            'text-white bg-blue': selected && isFirstRow,
          })}
          style={{ width: '80%', maxWidth: '1px' }}
        >
          {rowValue}
        </td>
      </tr>
    );
  };

  const populateTable = (sim) => {
    return (
      <table className="table table-transposed w-100 m-0">
        <tbody>
          {populateTableRow(
            'Name',
            sim.slotConfig.cardName,
            sim.selected,
            sim.slotName,
            true,
            true,
          )}
          {populateTableRow(
            'APN',
            sim.slotConfig.apn,
            sim.selected,
            sim.slotName,
            true,
          )}
          {sim.selected && populateTableRow('ICCID', sim.iccid)}
          {sim.selected && populateTableRow('IMSI', sim.imsi)}
          {sim.selected && populateTableRow('Home Network', sim.homeNetwork)}
          {!(
            sim.slotState === slotStateNotSelected ||
            sim.slotState === slotStateSelected
          ) && (
            <tr>
              <td colSpan="2" className="p-0 pt-2 border-0 text-danger">
                <Icon
                  name="danger-filled"
                  role="status"
                  className="h3 m-0 mx-2"
                />
                <span className="py-2">
                  {sim.slotState === slotStateError
                    ? slotStateErrorMsg
                    : slotStateUnknownMessage}
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  if (simSlots.length === 0) {
    return <strong>Please wait, we are fetching SIM details…</strong>;
  }
  return (
    <div>
      {simSlots.length > 1 && (
        <>
          <div className="d-flex justify-content-between">
            <div className="p-2 font-weight-bold">Active SIM</div>
            {!isSmallScreen && !readOnly && (
              <div className="d-flex align-items-center flex-grow-1">
                {populateSelectBox()}
              </div>
            )}
            {!readOnly && (
              <div className="px-2 py-1 d-flex justify-content-between">
                <Button
                  variant="link"
                  onClick={() => openSimInstructionsModal()}
                  className="border-0 p-0"
                >
                  <Icon name="question" role="status" className="h3 mb-0" />
                </Button>
              </div>
            )}
          </div>
          {isSmallScreen && !readOnly && (
            <div className="flex-grow-1">{populateSelectBox()}</div>
          )}
        </>
      )}
      <div>
        {simSlots.length > 1 && !readOnly && (
          <div className="mt-2 px-2 pt-2 pb-1 d-flex justify-content-between font-weight-bold">
            Configure SIM Slots
          </div>
        )}
        <Card>
          <Card.Body className="p-0">
            {simSlots.map((sim, index) => {
              const canEdit =
                !readOnly &&
                !sim.slotConfig.isFixed &&
                (!isEditing || editingSimSlot !== sim.slotName);
              return (
                <div key={sim.slotConfig.cardName}>
                  <Formik
                    key={sim.slotKey}
                    onSubmit={({ name, apn }) => {
                      updateSimInfo(index, name.trim(), apn.trim());
                    }}
                    initialValues={{
                      selected: sim.selected,
                      name: sim.slotConfig.cardName || '',
                      apn: sim.slotConfig.apn || '',
                    }}
                    enableReinitialize
                    validationSchema={simSchema}
                    validateOnChange
                  >
                    {({ handleSubmit, resetForm, isValid, isSubmitting }) => {
                      const isDisabled = readOnly || !isValid || isSubmitting;
                      return (
                        <Form
                          noValidate
                          onSubmit={handleSubmit}
                          key={sim.slotKey}
                        >
                          <div
                            key={sim.slotKey}
                            className={classNames(
                              'border',
                              index === 0 ? '' : 'border-top-0',
                            )}
                          >
                            <div className="d-flex justify-content-between">
                              <div className="p-2 d-flex justify-content-center align-items-start">
                                <span className="d-flex align-items-center">
                                  <Icon
                                    name={
                                      simIcons[sim.slotConfig.slotKey] || 'bdu'
                                    }
                                    className="text-gray-500 h3 mb-0"
                                  />
                                </span>
                              </div>
                              {!isSmallScreen && (
                                <div className="px-0 py-2 d-flex flex-grow-1 justify-content-center">
                                  {populateTable(sim)}
                                </div>
                              )}
                              <div
                                className={classNames(
                                  'py-1 px-2 d-flex justify-content-right align-items-start',
                                )}
                              >
                                <EditIcon
                                  canEdit={canEdit}
                                  slotName={sim.slotName}
                                  onEditSimSlot={(name) => {
                                    resetForm();
                                    editSimSlot(name);
                                  }}
                                  iconClass="mt-2 mb-0 mx-1 p-0 h5"
                                  className="text-primary p-0"
                                />
                              </div>
                            </div>
                            {isSmallScreen && (
                              <div className="p-2 d-flex flex-grow-1 justify-content-center">
                                {populateTable(sim)}
                              </div>
                            )}
                            {isEditing && editingSimSlot === sim.slotName && (
                              <div className="w-100 pb-2 d-flex flex-grow-1 justify-content-center">
                                <Button
                                  variant="link"
                                  className="text-capitalize mx-2"
                                  onClick={() => {
                                    resetForm();
                                    editSimSlot(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="submit"
                                  className={classNames(
                                    'mx-2 btn',
                                    isDisabled
                                      ? 'btn-secondary'
                                      : 'btn-primary',
                                  )}
                                  disabled={isDisabled}
                                >
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      </div>
      {activateSimModal && (
        <ActivateSimModal
          modal={activateSimModal}
          onHide={onHideActivateSimModal}
          onSlotSelectAndPowerOff={onSlotSelectAndPowerOff}
          onSlotSelectAndReboot={onSlotSelectAndReboot}
          slotInfo={getSlotInfo(selectedSimSlot)}
        />
      )}
      {simInstructionsModal && (
        <SimInstructionsModal
          modal={simInstructionsModal}
          onHide={setSimInstructionsModal}
        />
      )}
    </div>
  );
};

SimCards.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  slotStatus: PropTypes.arrayOf(
    PropTypes.shape({
      slotName: PropTypes.string.isRequired,
      slotState: PropTypes.oneOf(validSlotStates).isRequired,
      selected: PropTypes.bool,
      slotConfig: PropTypes.shape({
        cardName: PropTypes.string,
        apn: PropTypes.string,
        slotKey: PropTypes.oneOf(validSlotKeys).isRequired,
        isFixed: PropTypes.bool,
      }).isRequired,
      imsi: PropTypes.string,
      iccid: PropTypes.string,
      homeNetwork: PropTypes.string,
    }).isRequired,
  ).isRequired,
  onSlotUpdate: PropTypes.func.isRequired,
  onSlotSelectAndReboot: PropTypes.func.isRequired,
  onSlotSelectAndPowerOff: PropTypes.func.isRequired,
};

export default SimCards;
